import { type MouseEvent, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { navigate } from 'vike/client/router';

import { MoreMenu } from '@/components/Nav/MoreMenu';
import { closeMoreSheet, isMoreSheetOpen, subscribeMoreSheet } from '@/renderer/utils/moreSheet';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** Matches the `duration-*` on the panel and backdrop below. */
const ANIMATION_MS = 260;

/** How far (px) a downward drag has to travel before letting go dismisses. */
const DISMISS_DISTANCE = 110;
/** ...or how fast (px/ms) a shorter flick has to be moving. */
const DISMISS_VELOCITY = 0.5;

export function useMoreSheetOpen() {
  return useSyncExternalStore(subscribeMoreSheet, isMoreSheetOpen, () => false);
}

/**
 * The phone "More" menu as a bottom sheet over the current page (#2142).
 *
 * Dimmed backdrop, drag handle, no title. Closes on a backdrop tap, a swipe
 * down (from the handle, or from the list once it is scrolled to the top),
 * Escape, the back button (see renderer/utils/moreSheet.ts) and after an item
 * is picked. The list scrolls inside the sheet, which stops short of the status
 * bar and pads its end by the bottom safe area, so the last row (Sign out, the
 * legal links) always clears the home indicator.
 *
 * It sits above the tab bar (z-30) and the desktop sidebar (z-40); the alert
 * toasts (z-50, later in the DOM) still show over it. Portaled to <body> so a
 * transformed ancestor can't trap its `position: fixed`.
 */
export function MoreSheet() {
  const open = useMoreSheetOpen();
  // Stays mounted for the exit animation after `open` goes false. The entry is
  // a CSS keyframe (`ras-sheet-in` in renderer/styles/index.css), so it needs no
  // extra render or animation frame to start.
  const [closing, setClosing] = useState(false);
  const wasOpen = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mounted = open || closing;

  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      setClosing(false);
      return;
    }
    if (!wasOpen.current) {
      return;
    }
    wasOpen.current = false;
    setClosing(true);
    const timer = setTimeout(() => setClosing(false), ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // How the sheet is being opened: a tap or click (pointer) or a key press
  // (keyboard, or assistive tech that activates without a pointer). Tracked
  // from the document in the capture phase, so it is current by the time the
  // More tab's click opens the sheet.
  const openedByPointer = useRef(false);
  useEffect(() => {
    const onPointerDown = () => {
      openedByPointer.current = true;
    };
    const onKeyDown = () => {
      openedByPointer.current = false;
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  // Escape, focus, and pinning the page behind the sheet while it is open.
  // Opened from the keyboard, focus moves to the sheet's (visually hidden)
  // Close button, the first focusable element, which then shows. Opened by a
  // tap it goes to the dialog itself instead: WebKit can treat that programmatic
  // focus as focus-visible and paint the Close button over the sheet on a touch
  // open (#2142). VoiceOver still lands in the dialog and reaches Close as its
  // first element. Tab / Shift+Tab cycle inside the sheet either way: it is
  // aria-modal and the page behind it, tab bar included, is covered. On close
  // focus returns to the More tab that opened it, but only for a keyboard open:
  // after a tap, WebKit paints that programmatic focus as a focus ring around
  // the More tab, so a touch close just lets focus go.
  useEffect(() => {
    if (!open) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const byPointer = openedByPointer.current;
    const panel = panelRef.current;
    const closeButton = panel?.querySelector<HTMLElement>('[data-sheet-close]');
    ((openedByPointer.current ? null : closeButton) ?? panel)?.focus({
      preventScroll: true,
    });
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMoreSheet();
        return;
      }
      if (event.key === 'Tab' && panel) {
        const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) {
          return;
        }
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === panel || !panel.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      root.style.overflow = previousOverflow;
      if (byPointer) {
        (document.activeElement as HTMLElement | null)?.blur?.();
        return;
      }
      const moreTab = document.querySelector<HTMLElement>('#ras-tabbar [aria-haspopup="dialog"]');
      (moreTab ?? previouslyFocused)?.focus?.({ preventScroll: true });
    };
  }, [open]);

  // Swipe down to dismiss. Native listeners because the move handler has to
  // call preventDefault() (to stop the page rubber-banding instead), which
  // React's passive touch listeners can't.
  useEffect(() => {
    const panel = panelRef.current;
    if (!mounted || !panel) {
      return;
    }

    let startY = 0;
    let lastY = 0;
    let lastTime = 0;
    let velocity = 0;
    let canDrag = false;
    let dragging = false;

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch || event.touches.length > 1) {
        canDrag = false;
        return;
      }
      startY = lastY = touch.clientY;
      lastTime = event.timeStamp;
      velocity = 0;
      dragging = false;
      // From the handle always; from the list only when it can't scroll up any
      // further, so a downward swipe mid-list still scrolls the list.
      const fromHandle = (event.target as Element | null)?.closest?.('[data-sheet-handle]');
      canDrag = !!fromHandle || (scrollRef.current?.scrollTop ?? 0) <= 0;
    };

    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!canDrag || !touch) {
        return;
      }
      const dy = touch.clientY - startY;
      if (!dragging && dy <= 0) {
        // An upward swipe is a scroll, not a dismiss.
        canDrag = false;
        return;
      }
      dragging = true;
      event.preventDefault();
      const dt = event.timeStamp - lastTime;
      if (dt > 0) {
        velocity = (touch.clientY - lastY) / dt;
      }
      lastY = touch.clientY;
      lastTime = event.timeStamp;
      panel.style.transition = 'none';
      panel.style.transform = `translateY(${Math.max(0, dy)}px)`;
    };

    const onEnd = () => {
      if (!dragging) {
        return;
      }
      dragging = false;
      const dy = lastY - startY;
      const flicked = velocity > DISMISS_VELOCITY;
      panel.style.transition = '';
      if (dy > DISMISS_DISTANCE || (flicked && dy > 24)) {
        panel.style.transform = 'translateY(100%)';
        closeMoreSheet();
      } else {
        panel.style.transform = '';
      }
    };

    panel.addEventListener('touchstart', onStart, { passive: true });
    panel.addEventListener('touchmove', onMove, { passive: false });
    panel.addEventListener('touchend', onEnd);
    panel.addEventListener('touchcancel', onEnd);

    return () => {
      panel.removeEventListener('touchstart', onStart);
      panel.removeEventListener('touchmove', onMove);
      panel.removeEventListener('touchend', onEnd);
      panel.removeEventListener('touchcancel', onEnd);
      panel.style.transition = '';
      panel.style.transform = '';
    };
  }, [mounted]);

  // Picking an item: close first (which pops the sheet's history entry), then
  // navigate, so the sheet never leaves a dead entry behind the new page.
  // Anything the browser should handle itself (new tab, modified click,
  // another origin) is left alone.
  const onPanelClick = (event: MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as Element).closest?.('a[href]') as HTMLAnchorElement | null;
    if (
      !anchor ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (anchor.target && anchor.target !== '_self') ||
      anchor.hasAttribute('download') ||
      anchor.origin !== window.location.origin
    ) {
      return;
    }

    event.preventDefault();
    // Vike intercepts link clicks on `document` and does not check
    // preventDefault; stop the click here (the portal root) so only the
    // close-then-navigate below runs.
    event.stopPropagation();
    const href = anchor.pathname + anchor.search + anchor.hash;
    closeMoreSheet(() => {
      void navigate(href);
    });
  };

  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50" data-more-sheet="">
      <div
        aria-hidden="true"
        onClick={() => closeMoreSheet()}
        className={twMerge(
          'absolute inset-0 bg-black/40 transition-opacity duration-[260ms] ease-out',
          open ? 'animate-[ras-fade-in_260ms_ease-out]' : 'opacity-0',
        )}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="More"
        tabIndex={-1}
        onClick={onPanelClick}
        className={twMerge(
          // Stops short of the status bar so the page (and the backdrop) stay
          // visible above it, which is what makes it read as a sheet.
          'absolute inset-x-0 bottom-0 mx-auto flex max-h-[calc(100dvh-var(--safe-top)-2.5rem)] w-full max-w-xl flex-col rounded-t-[20px] bg-zinc-50 shadow-[0_-8px_30px_rgba(0,0,0,0.18)] outline-none transition-transform duration-[260ms] ease-[cubic-bezier(0.32,0.72,0,1)] dark:bg-zinc-950 dark:ring-1 dark:ring-white/10',
          open ? 'animate-[ras-sheet-in_260ms_cubic-bezier(0.32,0.72,0,1)]' : 'translate-y-full',
        )}
      >
        {/* Swipe, the backdrop and Escape are out of reach for VoiceOver and
            Switch Control users, so the sheet carries its own Close control.
            It only shows when reached by keyboard (focus-visible); a tap that
            opens the sheet focuses the dialog instead, so it never draws. */}
        <button
          type="button"
          data-sheet-close=""
          onClick={() => closeMoreSheet()}
          className="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:top-2 focus-visible:right-3 focus-visible:z-10 focus-visible:rounded-full focus-visible:bg-white focus-visible:px-3 focus-visible:py-1 focus-visible:font-medium focus-visible:text-sm focus-visible:text-zinc-900 focus-visible:shadow dark:focus-visible:bg-zinc-800 dark:focus-visible:text-zinc-100"
        >
          Close
        </button>
        <div data-sheet-handle="" className="flex shrink-0 touch-none justify-center pt-2 pb-3">
          <span
            aria-hidden="true"
            className="h-[5px] w-9 rounded-full bg-zinc-300 dark:bg-zinc-700"
          />
        </div>
        <div
          ref={scrollRef}
          data-sheet-scroll=""
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(1.25rem+var(--safe-bottom))]"
        >
          <MoreMenu />
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default MoreSheet;
